import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Box, Button, Card, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Fragment, SyntheticEvent, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useBlocker, useLocation } from 'react-router';
import { InferType } from 'yup';

import { WarningRounded } from '@mui/icons-material';
import {
  EmptyContent,
  enqueueSnackbar,
  FormProvider,
  FullPageSpinner,
  useBoolean,
  useDefaultEnumsStore,
  useDirtyFieldsStore,
  useFactFindConfigStore,
  useFactFindErrorStore,
  useFactFindProcessStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { PlusIcon } from '@zeta-core/assets';
import {
  AIInteractionService,
  IFactFindProcessSectionType,
  IFactFindProcessStatus,
  InteractionSubSectionType,
  InvestorService,
  IPatchAIInteractionSectionRequest,
  QueryAPIs,
} from '@zeta-services';
import {
  FactFindType,
  IClientCardItem,
  IFactFindEntity,
  ILiabilitiesAll,
  ILiabilitiesCapturePayload,
  ILiabilitiesFactFindResponse,
  IViewFactFindRequest,
  IViewFinancialSnapshotRequest,
  IViewFinancialSnapshotResponse,
} from '@zeta-types';
import { ConflictsDialog } from '../../../components/conflicts-dialog';
import { getClientFeatureAccess } from '../../../helpers/access-control-helper';
import AllTranscripts from '../all-transcripts';
import { getLabel } from '../common/commons';
import FactFindHeading from '../common/fact-find-heading';
import ConfirmNavigationDialog from '../components/confirmation-dialog';
import ErrorDialog from '../components/error-dialog';
import { ErrorSection } from '../components/error-section';
import SaveDialog from '../components/save-dialog';
import SimpleFieldsTable from '../components/simple-fields-table';
import SuccessDialog from '../components/success-dialog';
import SummaryTabComponent from '../components/summary-tab-component';
import FactFindFooter from '../fact-find-layout/fact-find-footer';
import { useSourceControlContext } from '../fact-find-layout/sourcecontrol-context-component';
import Transcript from '../transcript';
import { liabilitiesDefaultValues, liabilitiesValidationSchema, LIABILITY_TABLE_STRUCTURE } from './form-helpers';
import { allLiabilitiesTransform, createLiabilityCapturePartialPayload } from './transform';

export const Liabilities = ({
  isClient,
  isManagedClient,
  from,
  refetch,
  clientData,
  conflictData,
  clientSummary,
  updatedClientPartnerDetails,
}: {
  isClient?: boolean;
  isManagedClient?: boolean;
  from: FactFindType;
  refetch?: () => void;
}) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const location = useLocation();
  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  let state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  if (isClient) {
    state = { clientData: { clientID: accessibleEntityID } };
  }
  const {
    data: sourceControlData,
    mutateAsync: sourceControlMutateAsync,
    isPending: sourceControlPending,
    clientPartnerDetails,
  } = useSourceControlContext() || {};

  const isAllTranscripts = useBoolean();
  const isTranscript = useBoolean();
  const [currentTab, setCurrentTab] = useState<'client' | 'partner' | 'joint' | 'summary'>('summary');
  const updateDirtyFieldsState = useDirtyFieldsStore((state) => state.updateDirtyFieldsState);
  const defaultEnums = useDefaultEnumsStore((state) => state.defaultEnums);
  const frequencyOptions =
    defaultEnums?.frequencyType?.map((e) => {
      return { label: e.frequencyType, value: e.frequencyType };
    }) ?? [];

  const [confirmNavigationModal, setConfirmNavigationModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);

  const ffConfig = useFactFindConfigStore((state) => state?.factFindConfigDetails?.individualFactFindSectionConfig?.liabilities);
  const ffErrors = useFactFindErrorStore((state) => state?.ffErrors);
  const updateFFErrorStore = useFactFindErrorStore((state) => state?.updateFFErrorStore);
  const fieldErrors = ffErrors?.find((error) => error.section === InteractionSubSectionType.liabilities_details)?.missing_fields;
  const sectionsProcessDetails = useFactFindProcessStore((state) => state.sections);
  const currentSection = sectionsProcessDetails?.find((section) => section.section_name === IFactFindProcessSectionType.liabilities)?.status;
  const planningStatus = location?.state?.clientData?.planningStatus;
  const readOnly = getClientFeatureAccess(userAccess?.access, 'Advisor-Client-FF', planningStatus).isReadOnly;
  const isReadOnly =
    from === FactFindType.PARA_PLANNER ||
    currentSection === IFactFindProcessStatus.pending ||
    currentSection === IFactFindProcessStatus.started ||
    isClient ||
    readOnly;

  type liabilitiesSchemaType = InferType<typeof liabilitiesValidationSchema>;
  const methods = useForm<liabilitiesSchemaType>({
    defaultValues: liabilitiesDefaultValues(ffConfig),
    resolver: yupResolver(liabilitiesValidationSchema),
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const { reset } = methods;
  const fieldArrayClient = useFieldArray({ name: 'client', control: methods.control });
  const fieldArrayPartner = useFieldArray({ name: 'partner', control: methods.control });
  const fieldArrayJoint = useFieldArray({ name: 'joint', control: methods.control });

  const fetchFieldArray = (tab: 'client' | 'partner' | 'joint') => {
    switch (tab) {
      case 'client':
        return fieldArrayClient;
      case 'partner':
        return fieldArrayPartner;
      case 'joint':
        return fieldArrayJoint;
    }
  };

  const handleChangeTab = (event: SyntheticEvent, value: 'client' | 'partner' | 'joint') => {
    setCurrentTab(value);
  };

  const [summaryData, setSummaryData] = useState({});
  const [conflictsDialog, setConflictsDialog] = useState<boolean>(false);

  console.log(conflictsDialog, 'CDDD');

  const calculateSummary = useMutation({
    mutationFn: (payload: IViewFinancialSnapshotRequest) => QueryAPIs().viewFinancialSnapshot(payload),

    onSuccess: (response: AxiosResponse<IViewFinancialSnapshotResponse>) => {
      const { data: responseData } = response;

      const summary = {
        totalClientValue: responseData?.client?.liabilityAmount,
        totalPartnerValue: responseData?.partner?.liabilityAmount,
        totalJointValue: responseData?.joint?.liabilityAmount,
        total: responseData?.all?.liabilityAmount,
      };

      setSummaryData(summary);
    },
  });

  const viewLiabilities = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => InvestorService().viewLiabilities(payload),

    onSuccess: (response: AxiosResponse<ILiabilitiesFactFindResponse>) => {
      const { data: responseData } = response;
      const liabilities: ILiabilitiesAll = responseData.response?.liabilities;

      const allLiabilities = allLiabilitiesTransform(liabilities, ffConfig);

      methods.reset(allLiabilities);
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      calculateSummary.mutateAsync({ clientID: state?.clientData?.clientID, partnerID: clientPartnerDetails?.partnerDetail?.id ?? '' });
      viewLiabilities.mutateAsync({ clientID: state.clientData.clientID, forExternalUse: true });
    }
    if (from === FactFindType.AI) {
      const clientSummaryData = {
        totalClientValue: clientSummary?.client?.liabilityAmount,
        totalPartnerValue: clientSummary?.partner?.liabilityAmount,
        totalJointValue: clientSummary?.joint?.liabilityAmount,
        total: clientSummary?.all?.liabilityAmount,
      };

      setSummaryData(clientSummaryData);
      const liabilities: ILiabilitiesAll = clientData?.response?.liabilities;
      const allLiabilities = allLiabilitiesTransform(liabilities, ffConfig);
      reset(allLiabilities);
    }
  }, [reset, clientData?.response?.liabilities, clientSummary, from, state.clientData.clientID]);

  const isSuccess = useBoolean(false);
  const isError = useBoolean(false);

  const isDirtyInPage = methods.formState.isDirty;

  useEffect(() => {
    if (isDirtyInPage) {
      updateDirtyFieldsState(true);
    }
  }, [isDirtyInPage, updateDirtyFieldsState]);

  const blocker = useBlocker(({ nextLocation, currentLocation }) => {
    return isDirtyInPage && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked' && showPrompt) {
      setConfirmNavigationModal(true);
    } else {
      blocker?.proceed?.();
    }
  }, [blocker, showPrompt]);

  const handleDiscardAndProceed = () => {
    setConfirmNavigationModal(false);
    blocker?.proceed?.();
    setShowPrompt(false);
    updateDirtyFieldsState(false);
  };

  const handleCloseNavigationModal = () => {
    setConfirmNavigationModal(false);
    blocker?.reset?.();
    setShowPrompt(true);
  };

  const captureLiabilitiesApi = useMutation({
    mutationFn: (payload: ILiabilitiesCapturePayload) => InvestorService().captureLiabilities(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      viewLiabilities.mutateAsync({ clientID: state.clientData.clientID, forExternalUse: true });
      sourceControlMutateAsync({ clientID: state.clientData.clientID });
      calculateSummary.mutateAsync({ clientID: state?.clientData?.clientID, partnerID: clientPartnerDetails?.partnerDetail?.id ?? '' });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Liabilities saved successfully', { variant: 'success' });
      updateFFErrorStore({ ffErrors: null });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const captureLiabilitiesApiAI = useMutation({
    mutationFn: (payload: IPatchAIInteractionSectionRequest) => AIInteractionService().patchClientData(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      refetch?.();
      sourceControlMutateAsync({ clientID: state.clientData.clientID });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Liabilities saved successfully', { variant: 'success' });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const onSubmit = (formData: liabilitiesSchemaType) => {
    const payload = {
      clientID: state.clientData.clientID,
      clientLiability: createLiabilityCapturePartialPayload(formData.client || []),
      partnerLiability: createLiabilityCapturePartialPayload(formData.partner || []),
      jointLiability: createLiabilityCapturePartialPayload(formData.joint || []),
    };
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      captureLiabilitiesApi.mutateAsync(payload);
    }
    if (from === FactFindType.AI) {
      const payloadAI = {
        section: InteractionSubSectionType.liabilities_details,
        client_id: state.clientData.clientID,
        data: { response: { liabilities: payload } },
      };
      captureLiabilitiesApiAI.mutate(payloadAI);
    }
  };

  const tabSections: ('summary' | 'joint' | 'client' | 'partner')[] = ['summary', 'client', 'partner', 'joint'];

  const getClientsOptions = tabSections.map((type) => ({
    label: getLabel(
      { value: type, label: type.charAt(0).toUpperCase() + type.slice(1) },
      [FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from) && !isClient ? clientPartnerDetails : updatedClientPartnerDetails
    ),
    value: type,
  }));

  return (
    <Box>
      {isTranscript.value && <Transcript section="Liabilities" isTranscript={isTranscript} />}

      {isAllTranscripts.value && <AllTranscripts isAllTranscripts={isAllTranscripts} />}

      {!isAllTranscripts.value && !isTranscript.value && (
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          {viewLiabilities.isPending || sourceControlPending ? (
            <FullPageSpinner />
          ) : (
            <>
              <Stack direction="row" alignItems="center">
                <FactFindHeading
                  sectionName="Liabilities"
                  isAllTranscripts={isAllTranscripts}
                  isTranscript={isTranscript}
                  hideButton={sourceControlData?.dataSourceMap?.Liabilities === 'ManualInput'}
                  hideAllButton={isClient || [FactFindType.AI, FactFindType.PARA_PLANNER].includes(from) || isManagedClient}
                />
                {conflictData?.length > 0 && (
                  <Tooltip title="Click here to view all the conflicts">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        setConflictsDialog(true);
                      }}
                      sx={{ marginTop: '-12px' }}
                    >
                      <WarningRounded />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {fieldErrors?.length > 0 && <ErrorSection errors={fieldErrors} />}
              <Stack sx={{ flexDirection: 'row', alignItems: 'center', mb: 2.5 }}>
                <Autocomplete
                  options={
                    from === FactFindType.PARA_PLANNER ? getClientsOptions.filter((e) => !['Client', 'Partner', 'Joint'].includes(e.label)) : getClientsOptions
                  }
                  id="liabilitiesDropdown"
                  value={getClientsOptions.find((option) => option.value === currentTab)?.label ?? ''}
                  sx={{ width: 300 }}
                  onChange={(e, v) => handleChangeTab(e, v?.value)}
                  getOptionLabel={(option) => option?.label || option}
                  renderInput={(params) => <TextField {...params} />}
                  disableClearable={true}
                />
              </Stack>

              <Stack spacing={1} marginTop={2}>
                {currentTab === 'summary' && (
                  <SummaryTabComponent
                    section="Liabilities"
                    clientPartnerDetails={from === FactFindType.AI ? updatedClientPartnerDetails : clientPartnerDetails}
                    summaryData={summaryData}
                  />
                )}

                {['client', 'partner', 'joint'].map(
                  (tab) =>
                    currentTab === tab && (
                      <Fragment key={tab}>
                        {isReadOnly && Object.keys(viewLiabilities?.data?.data?.response?.liabilities || {})?.length === 0 && (
                          <Card>
                            <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                              <EmptyContent description={`No liabilities information has been captured as part of the fact-finding process.`} />
                            </Stack>
                          </Card>
                        )}

                        {((isReadOnly && Object.keys(viewLiabilities?.data?.data?.response?.liabilities || {})?.length > 0) ||
                          fetchFieldArray(tab).fields?.length > 0) && (
                          <SimpleFieldsTable
                            fieldArray={fetchFieldArray(tab).fields}
                            currentTab={currentTab}
                            onRemove={fetchFieldArray(tab).remove}
                            tableDetails={LIABILITY_TABLE_STRUCTURE(frequencyOptions)}
                            isClient={isClient}
                            isReadOnly={isReadOnly}
                          />
                        )}
                        {isClient || isReadOnly ? null : (
                          <Stack flexDirection="row" marginTop={1}>
                            <Button
                              variant="text"
                              color="primary"
                              sx={{ marginLeft: 1 }}
                              size="small"
                              startIcon={<PlusIcon />}
                              onClick={() => {
                                const appendFunction = fetchFieldArray(tab).append;

                                appendFunction({
                                  liabilityType: '',
                                  institution: '',
                                  originalAmount: 0,
                                  monthlyPayment: 0,
                                  paymentFrequency: '',
                                  startDate: '',
                                  endDate: '',
                                  interest: '',
                                  currentValue: 0,
                                  collateral: '',
                                  securedOrNot: '',
                                  description: '',
                                  show: true,
                                });
                              }}
                            >
                              Add Liabilities
                            </Button>
                          </Stack>
                        )}
                      </Fragment>
                    )
                )}
              </Stack>
            </>
          )}
          {isClient ? null : (
            <>
              {[FactFindType.FACT_FIND, FactFindType.AI].includes(from) && (
                <FactFindFooter isManagedClient={isManagedClient} from={from} hideNextStep={from === FactFindType.AI} hideSave={isReadOnly} />
              )}
              <SaveDialog open={captureLiabilitiesApi.isPending} />
              <SuccessDialog open={isSuccess.value} />
              <ErrorDialog open={isError.value} />
              <ConfirmNavigationDialog open={confirmNavigationModal} handleClose={handleCloseNavigationModal} data={{ handleDiscardAndProceed, blocker }} />
              <ConflictsDialog
                open={conflictsDialog}
                handleClose={() => setConflictsDialog(false)}
                tableData={conflictData || []}
                isPending={false}
                title={'Liabilities'}
                refetch={refetch}
              />
            </>
          )}
        </FormProvider>
      )}
    </Box>
  );
};
